import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Toolbar, CircularProgress, useMediaQuery } from "@mui/material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../../shared/hooks/useRedesignSetup";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import useDataRefresh from "../../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../../shared/hooks/useVisibilityRefresh";
import PageBreadcrumbs from "../../../../shared/components/PageBreadcrumbs";
import { clearOffer } from "../../customerOffersSlice";
import { Offer, OfferStatusCode, OfferCustomer } from "../../models/models";
import { ReceivedOffer, OfferProvider } from "../../../servicing/models/receivedOffersModels";
import ServiceLifecycleTracker from "../../../shared/components/ServiceLifecycleTracker";
import { getProviderLifecycleStages, getOwnerLifecycleStages } from "../../../shared/utils/lifecycleStages";
import ConfirmServiceDialog from "../ConfirmServiceDialog";
import OfferHeaderStrip from "./OfferHeaderStrip";
import OfferPricingCard from "./OfferPricingCard";
import OfferTimelineCard from "./OfferTimelineCard";
import OfferApproachCard from "./OfferApproachCard";
import OfferIncludedServicesCard from "./OfferIncludedServicesCard";
import OfferTermsCard from "./OfferTermsCard";
import OfferMessageCard from "./OfferMessageCard";
import OfferAttachmentsCard from "./OfferAttachmentsCard";
import OfferRfsContextCard from "./OfferRfsContextCard";
import OfferCustomerCard from "./OfferCustomerCard";
import OfferProviderCard from "./OfferProviderCard";
import OfferMachineCard from "./OfferMachineCard";
import OfferActionBar from "./OfferActionBar";
import OfferStatusGuidanceBanner from "./OfferStatusGuidanceBanner";
import OfferLogbookSection from "./OfferLogbookSection";
import OfferMiniMap from "./OfferMiniMap";
import offersApi from "../../api/offersApi";
import receivedOffersApi from "../../../servicing/api/receivedOffersApi";
import { hideAccessDenied } from "../../../../public/accessDeniedSlice";

// ===================================================================
//  KEYFRAMES
// ===================================================================
const DETAIL_KEYFRAMES_ID = "offer-detail-keyframes";
const DETAIL_KEYFRAMES = `
  @keyframes heroReveal {
    0%   { opacity: 0; transform: translateY(-12px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes sectionReveal {
    0%   { opacity: 0; transform: translateY(16px) scale(0.98); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
  @media print {
    /* Hide navigation, action bars, maps, breadcrumbs, share buttons */
    nav, .MuiToolbar-root, .MuiDrawer-root, .MuiAppBar-root,
    [data-print-hide="true"] {
      display: none !important;
    }
    /* Reset page background */
    body, #root { background: white !important; }
    /* Force single column, full width */
    * {
      animation: none !important;
      transition: none !important;
    }
    /* Remove dot pattern background */
    [style*="backgroundImage"] {
      background-image: none !important;
    }
  }
`;

/**
 * Normalises a ReceivedOffer (owner perspective) into the Offer shape
 * so that shared sub-components (pricing, timeline, terms, etc.) work unchanged.
 */
function normaliseReceivedOfferToOffer(ro: ReceivedOffer): Offer {
  return {
    id: ro.id,
    internalNumber: ro.internalNumber,
    statusCode: ro.statusCode,
    totalEstimatedCost: ro.totalEstimatedCost,
    serviceRequest: {
      id: ro.serviceRequestId,
      internalNumber: "",
      issue: "",
      machine: { id: "", name: "" },
    },
    customer: {
      id: ro.provider?.id || "",
      name: ro.provider?.name || "",
    },
    laborCost: ro.laborCost,
    laborHours: ro.laborHours,
    partsCost: ro.partsCost,
    travelCost: ro.travelCost,
    travelDistanceKm: ro.travelDistanceKm,
    travelRatePerKm: ro.travelRatePerKm,
    travelTimeHours: ro.travelTimeHours,
    travelRatePerHour: ro.travelRatePerHour,
    additionalCosts: ro.additionalCosts,
    earliestStartDate: ro.earliestStartDate,
    immediateStart: false,
    estimatedDuration: ro.estimatedDuration,
    completionTimeCode: ro.completionTimeCode,
    customCompletionTime: ro.customCompletionTime,
    serviceApproach: ro.serviceApproach,
    specialEquipmentRequired: ro.specialEquipmentRequired,
    warrantyPeriodCode: ro.warrantyPeriodCode,
    customWarrantyPeriod: ro.customWarrantyPeriod,
    paymentTermsCode: ro.paymentTermsCode,
    customPaymentTerms: ro.customPaymentTerms,
    includeDiagnostics: ro.includeDiagnostics,
    includeLaborForRepair: ro.includeLaborForRepair,
    includePartsAndMaterials: ro.includePartsAndMaterials,
    includeTestingAndCalibration: ro.includeTestingAndCalibration,
    includeWorkAreaCleanup: ro.includeWorkAreaCleanup,
    includeDetailedServiceReport: ro.includeDetailedServiceReport,
    validityPeriodCode: ro.validityPeriodCode,
    messageToCustomer: ro.messageToCustomer,
    confirmedBindingOffer: false,
    confirmedTermsOfService: false,
    confirmedAuthorityToOffer: false,
    notifyOnViewAcceptReject: false,
    notifyTeamMembers: false,
    files: ro.files,
    expiresAt: ro.expiresAt,
    sentDate: ro.sentDate,
    viewedDate: ro.viewedDate,
    createdDate: String(ro.createdDate),
    modifiedDate: String(ro.createdDate),
    confirmedArrivalDate: ro.confirmedArrivalDate,
    confirmedArrivalTimeFrom: ro.confirmedArrivalTimeFrom,
    confirmedArrivalTimeTo: ro.confirmedArrivalTimeTo,
    confirmationDeadline: ro.confirmationDeadline,
    confirmationMessage: ro.confirmationMessage,
  } as Offer;
}

const OfferDetailPage: React.FC = () => {
  useRedesignSetup();
  const { translate } = useTranslation(["serviceOffers", "servicing", "common"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(min-width:769px) and (max-width:1200px)");
  const isDesktop = useMediaQuery("(min-width:1201px)");

  // State
  const [offer, setOffer] = useState<Offer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Owner detection
  const [isOwner, setIsOwner] = useState(false);
  const [ownerProvider, setOwnerProvider] = useState<OfferProvider | null>(null);
  const [ownerServiceRequestId, setOwnerServiceRequestId] = useState<string | null>(null);

  // Inject keyframes
  useEffect(() => {
    if (!document.getElementById(DETAIL_KEYFRAMES_ID)) {
      const style = document.createElement("style");
      style.id = DETAIL_KEYFRAMES_ID;
      style.textContent = DETAIL_KEYFRAMES;
      document.head.appendChild(style);
    }
  }, []);

  // Fetch offer — try provider endpoint first, fall back to owner
  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(false);

      // Step 1: Try provider endpoint
      try {
        const data = await offersApi.getOffer(id);
        setOffer(data);
        setIsOwner(false);
        setOwnerProvider(null);
        setOwnerServiceRequestId(null);
      } catch (providerErr: any) {
        // Step 2: Provider failed (403/404) — try owner endpoint
        dispatch(hideAccessDenied());
        try {
          const receivedOffer = await receivedOffersApi.getReceivedOfferById(id);
          setOwnerProvider(receivedOffer.provider);
          setOwnerServiceRequestId(receivedOffer.serviceRequestId);
          setIsOwner(true);
          // Normalise to Offer shape for sub-components
          setOffer(normaliseReceivedOfferToOffer(receivedOffer));
        } catch (ownerErr: any) {
          dispatch(hideAccessDenied());
          console.error("Failed to load offer (both perspectives):", ownerErr);
          setError(true);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [id, dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup Redux on unmount
  useEffect(() => {
    return () => {
      dispatch(clearOffer());
    };
  }, [dispatch]);

  // Real-time refresh
  useDataRefresh("myOffers", () => {
    if (id) fetchData();
  });

  useDataRefresh("receivedOffers", () => {
    if (id) fetchData();
  });

  useVisibilityRefresh(() => {
    if (id) fetchData();
  });

  // Handlers
  const handleDataRefresh = () => {
    if (id) fetchData();
  };

  const handleConfirmService = async (data: {
    arrivalDate: string;
    arrivalTimeFrom: string;
    arrivalTimeTo: string;
    message?: string;
  }) => {
    if (!offer) return;
    await offersApi.confirmOffer(offer.id, data);
    setConfirmDialogOpen(false);
    fetchData();
  };

  // Loading state
  if (loading && !offer) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress sx={{ color: dt.blue }} />
      </Box>
    );
  }

  if (error || !offer) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Box sx={{ fontSize: "14px", fontFamily: dt.font, color: dt.textSecondary }}>
          {translate("noOffersFound")}
        </Box>
      </Box>
    );
  }

  // Breadcrumbs — differ by perspective
  const breadcrumbItems = isOwner
    ? [
        { title: translate("servicing:myServiceRequests"), link: "/service-requests" },
        ...(ownerServiceRequestId
          ? [{ title: offer.serviceRequest?.internalNumber || translate("common:common.request"), link: `/service-board/${ownerServiceRequestId}` }]
          : []),
        { title: offer.internalNumber },
      ]
    : [
        { title: translate("myOffers"), link: "/service-offers" },
        { title: offer.internalNumber },
      ];

  const showLifecycleStepper = offer.statusCode !== OfferStatusCode.draft;

  // Lifecycle stepper — owner uses offer-aware provider stages (offer perspective, not SR)
  const renderLifecycleStepper = () => {
    if (!showLifecycleStepper) return null;
    return (
      <Box sx={{ marginBottom: "20px", animation: "sectionReveal 0.5s ease-out both" }}>
        <ServiceLifecycleTracker
          stages={getProviderLifecycleStages(offer.statusCode)}
          variant="full"
        />
      </Box>
    );
  };

  // Guidance banner
  const renderGuidanceBanner = () => {
    if (offer.statusCode === OfferStatusCode.draft) return null;
    return (
      <Box sx={{ marginBottom: "16px" }}>
        <OfferStatusGuidanceBanner
          statusCode={offer.statusCode}
          isOwner={isOwner}
          customerName={offer.customer?.name}
          providerName={ownerProvider?.name}
          confirmedArrivalDate={offer.confirmedArrivalDate}
          reviewRating={offer.reviewRating}
        />
      </Box>
    );
  };

  // Shared content cards (same for both perspectives)
  const renderContentCards = () => (
    <>
      <OfferPricingCard offer={offer} />
      <OfferTimelineCard offer={offer} />
      <OfferApproachCard offer={offer} />
      <OfferIncludedServicesCard offer={offer} />
      <OfferTermsCard offer={offer} />
      <OfferMessageCard message={offer.messageToCustomer} />
      <OfferAttachmentsCard files={offer.files} offerId={offer.id} />
    </>
  );

  // Logbook section — execution lifecycle (progress, completion, reviews)
  const renderLogbookSection = () => (
    <OfferLogbookSection
      offer={offer}
      isOwner={isOwner}
      serviceRequestId={ownerServiceRequestId || offer.serviceRequest?.id || undefined}
      onDataRefresh={handleDataRefresh}
    />
  );

  // ===================================================================
  //  PROVIDER DESKTOP LAYOUT: 25 / 50 / 25
  // ===================================================================
  const renderProviderDesktopLayout = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "25% 50% 25%",
        gap: 2.5,
        maxWidth: 1280,
        margin: "0 auto",
        px: 3,
        py: 2.5,
      }}
    >
      {/* LEFT COLUMN — Mini Map + Customer */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.1s both",
        }}
      >
        <OfferMiniMap
          counterpartLat={offer.customer?.latitude ? parseFloat(offer.customer.latitude) : undefined}
          counterpartLng={offer.customer?.longitude ? parseFloat(offer.customer.longitude) : undefined}
          counterpartName={offer.customer?.name || ""}
          counterpartLogoUrl={offer.customer?.logoUrl}
          counterpartCity={offer.customer?.city}
          isOwner={false}
          offerNumber={offer.internalNumber}
        />
        <OfferCustomerCard customer={offer.customer} />
      </Box>

      {/* CENTRE COLUMN */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          animation: "heroReveal 0.4s ease-out both",
        }}
      >
        {renderLifecycleStepper()}
        {renderGuidanceBanner()}
        <OfferHeaderStrip offer={offer} onDataRefresh={handleDataRefresh} />
        <OfferRfsContextCard serviceRequest={offer.serviceRequest} />
        {renderContentCards()}
        {renderLogbookSection()}
        <OfferActionBar
          offer={offer}
          isOwner={false}
          serviceRequestId={undefined}
          onConfirmServiceOpen={() => setConfirmDialogOpen(true)}
          onDataRefresh={handleDataRefresh}
        />
      </Box>

      {/* RIGHT COLUMN — Machine */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.15s both",
        }}
      >
        <OfferMachineCard machine={offer.serviceRequest?.machine} />
      </Box>
    </Box>
  );

  // ===================================================================
  //  OWNER DESKTOP LAYOUT: 70 / 30
  // ===================================================================
  const renderOwnerDesktopLayout = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "70% 30%",
        gap: 2.5,
        maxWidth: 1280,
        margin: "0 auto",
        px: 3,
        py: 2.5,
      }}
    >
      {/* MAIN COLUMN */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          animation: "heroReveal 0.4s ease-out both",
        }}
      >
        {renderLifecycleStepper()}
        {renderGuidanceBanner()}
        <OfferHeaderStrip offer={offer} onDataRefresh={handleDataRefresh} />
        <OfferRfsContextCard serviceRequest={offer.serviceRequest} />
        {renderContentCards()}
        {renderLogbookSection()}
        <OfferActionBar
          offer={offer}
          isOwner={true}
          serviceRequestId={ownerServiceRequestId || undefined}
          onConfirmServiceOpen={() => setConfirmDialogOpen(true)}
          onDataRefresh={handleDataRefresh}
        />
      </Box>

      {/* RIGHT COLUMN — Mini Map + Provider + Machine */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.1s both",
        }}
      >
        <OfferMiniMap
          counterpartLat={ownerProvider?.latitude ? parseFloat(ownerProvider.latitude) : undefined}
          counterpartLng={ownerProvider?.longitude ? parseFloat(ownerProvider.longitude) : undefined}
          counterpartName={ownerProvider?.name || ""}
          counterpartLogoUrl={ownerProvider?.logoUrl}
          counterpartCity={ownerProvider?.city}
          isOwner={true}
          offerNumber={offer.internalNumber}
        />
        <OfferProviderCard provider={ownerProvider || undefined} />
        <OfferMachineCard machine={offer.serviceRequest?.machine} />
      </Box>
    </Box>
  );

  // ===================================================================
  //  TABLET LAYOUT (both perspectives — single column)
  // ===================================================================
  const renderTabletLayout = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        maxWidth: 900,
        margin: "0 auto",
        px: 2,
        py: 2,
      }}
    >
      {renderLifecycleStepper()}
      {renderGuidanceBanner()}

      <Box sx={{ animation: "heroReveal 0.4s ease-out both" }}>
        <OfferHeaderStrip offer={offer} onDataRefresh={handleDataRefresh} />
      </Box>

      <Box sx={{ animation: "heroReveal 0.4s ease-out 0.05s both" }}>
        <OfferRfsContextCard serviceRequest={offer.serviceRequest} />
      </Box>

      {/* Mini Map */}
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.08s both" }}>
        <OfferMiniMap
          counterpartLat={isOwner
            ? (ownerProvider?.latitude ? parseFloat(ownerProvider.latitude) : undefined)
            : (offer.customer?.latitude ? parseFloat(offer.customer.latitude) : undefined)
          }
          counterpartLng={isOwner
            ? (ownerProvider?.longitude ? parseFloat(ownerProvider.longitude) : undefined)
            : (offer.customer?.longitude ? parseFloat(offer.customer.longitude) : undefined)
          }
          counterpartName={isOwner ? (ownerProvider?.name || "") : (offer.customer?.name || "")}
          counterpartLogoUrl={isOwner ? ownerProvider?.logoUrl : offer.customer?.logoUrl}
          counterpartCity={isOwner ? ownerProvider?.city : offer.customer?.city}
          isOwner={isOwner}
          offerNumber={offer.internalNumber}
        />
      </Box>

      {/* Counterpart + Machine side by side */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.1s both",
        }}
      >
        {isOwner ? (
          <OfferProviderCard provider={ownerProvider || undefined} />
        ) : (
          <OfferCustomerCard customer={offer.customer} />
        )}
        <OfferMachineCard machine={offer.serviceRequest?.machine} />
      </Box>

      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.15s both" }}>
        <OfferPricingCard offer={offer} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.17s both" }}>
        <OfferTimelineCard offer={offer} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.19s both" }}>
        <OfferApproachCard offer={offer} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.21s both" }}>
        <OfferIncludedServicesCard offer={offer} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.23s both" }}>
        <OfferTermsCard offer={offer} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.25s both" }}>
        <OfferMessageCard message={offer.messageToCustomer} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.27s both" }}>
        <OfferAttachmentsCard files={offer.files} offerId={offer.id} />
      </Box>

      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.29s both" }}>
        {renderLogbookSection()}
      </Box>

      {/* Spacer for fixed action bar */}
      <Box sx={{ height: 80 }} />
      <OfferActionBar
        offer={offer}
        isOwner={isOwner}
        serviceRequestId={ownerServiceRequestId || undefined}
        onConfirmServiceOpen={() => setConfirmDialogOpen(true)}
        onDataRefresh={handleDataRefresh}
      />
    </Box>
  );

  // ===================================================================
  //  PHONE LAYOUT (both perspectives — single column, compact)
  // ===================================================================
  const renderPhoneLayout = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        px: 1.5,
        py: 1.5,
      }}
    >
      {renderLifecycleStepper()}
      {renderGuidanceBanner()}

      <Box sx={{ animation: "heroReveal 0.4s ease-out both" }}>
        <OfferHeaderStrip offer={offer} onDataRefresh={handleDataRefresh} />
      </Box>
      <Box sx={{ animation: "heroReveal 0.4s ease-out 0.05s both" }}>
        <OfferRfsContextCard serviceRequest={offer.serviceRequest} />
      </Box>
      {/* On phone, show counterpart card instead of customer for owner */}
      {isOwner ? (
        <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.08s both" }}>
          <OfferProviderCard provider={ownerProvider || undefined} />
        </Box>
      ) : null}
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.1s both" }}>
        <OfferMachineCard machine={offer.serviceRequest?.machine} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.12s both" }}>
        <OfferPricingCard offer={offer} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.14s both" }}>
        <OfferTimelineCard offer={offer} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.16s both" }}>
        <OfferApproachCard offer={offer} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.18s both" }}>
        <OfferIncludedServicesCard offer={offer} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.20s both" }}>
        <OfferTermsCard offer={offer} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.22s both" }}>
        <OfferMessageCard message={offer.messageToCustomer} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.24s both" }}>
        <OfferAttachmentsCard files={offer.files} offerId={offer.id} />
      </Box>

      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.26s both" }}>
        {renderLogbookSection()}
      </Box>

      {/* Spacer for fixed action bar */}
      <Box sx={{ height: 72 }} />
      <OfferActionBar
        offer={offer}
        isOwner={isOwner}
        serviceRequestId={ownerServiceRequestId || undefined}
        onConfirmServiceOpen={() => setConfirmDialogOpen(true)}
        onDataRefresh={handleDataRefresh}
      />
    </Box>
  );

  // Choose desktop layout based on perspective
  const renderDesktopLayout = () => {
    if (isOwner) return renderOwnerDesktopLayout();
    return renderProviderDesktopLayout();
  };

  return (
    <>
      {/* Breadcrumbs — hidden on print */}
      <Toolbar className="flex items-center justify-between w-full" data-print-hide="true">
        <Box className="heading-2">
          <PageBreadcrumbs items={breadcrumbItems} />
        </Box>
      </Toolbar>

      {/* Page background */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          minHeight: "calc(100vh - 64px)",
          background: dt.pageBg,
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          backgroundImage: `radial-gradient(${dt.pageDotColor} 1px, transparent 1px)`,
          backgroundSize: "20px 20px",
          overflowX: "hidden",
        }}
      >
        {(isDesktop || (!isTablet && !isPhone)) && renderDesktopLayout()}
        {isTablet && renderTabletLayout()}
        {isPhone && renderPhoneLayout()}
      </Box>

      {/* Confirm Service Dialog — provider only */}
      {!isOwner && (
        <ConfirmServiceDialog
          open={confirmDialogOpen}
          offerNumber={offer.internalNumber}
          defaultArrivalDate={offer.earliestStartDate}
          onConfirm={handleConfirmService}
          onClose={() => setConfirmDialogOpen(false)}
        />
      )}
    </>
  );
};

export default OfferDetailPage;
